import {
	Alert,
	Button,
	Checkbox,
	FocusTrap,
	Group,
	Modal,
	Select,
	Stack,
	TagsInput,
	Text,
	TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { License } from "../../store/licenses/licensesSlice";
import { toDateInputValue, toExpirationTimestamp } from "../../utils/dates";
import ExpirationQuickSet from "./ExpirationQuickSet";

export interface LicenseFormData {
	name: string;
	accountId: string;
	os: string;
	licenseTypeId: string;
	isUnlimited: boolean;
	expirationDate: string;
	tags: string[];
}

interface LicenseModalProps {
	opened: boolean;
	license: License | null;
	loading: boolean;
	accounts: Array<{ value: string; label: string }>;
	licenseTypes: Array<{ value: string; label: string }>;
	onClose: () => void;
	onSubmit: (values: LicenseFormData) => void;
}

const OS_OPTIONS = [
	{ value: "Windows", label: "Windows" },
	{ value: "macOS", label: "macOS" },
	{ value: "Linux", label: "Linux" },
	{ value: "iOS", label: "iOS" },
	{ value: "Android", label: "Android" },
	{ value: "tvOS", label: "tvOS" },
	// Values must match the backend License.os enum
	{ value: "TizenOS", label: "Samsung Smart TV" },
	{ value: "WebOS", label: "LG Smart TV" },
];

const toDisplayDate = (value?: string | null) =>
	value ? new Date(value).toLocaleString("he-IL") : "-";

function LicenseModal({
	opened,
	license,
	loading,
	accounts,
	licenseTypes,
	onClose,
	onSubmit,
}: LicenseModalProps) {
	const [activeFocus, setActiveFocus] = useState(false);

	const form = useForm<LicenseFormData>({
		initialValues: {
			name: "",
			accountId: "",
			os: "",
			licenseTypeId: "",
			isUnlimited: false,
			expirationDate: toDateInputValue(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
			tags: [],
		},
		validate: {
			name: (value) =>
				value.length < 2 ? "Name must have at least 2 characters" : null,
			accountId: (value) => (!value ? "Account is required" : null),
			os: (value) => (!value ? "Operating system is required" : null),
			licenseTypeId: (value) => (!value ? "License type is required" : null),
			expirationDate: (value, values) => {
				if (values.isUnlimited) return null;
				if (!value) return "Expiration date is required";
				return new Date(toExpirationTimestamp(value)) <= new Date()
					? "Expiration date must be in the future"
					: null;
			},
		},
	});

	useEffect(() => {
		if (license) {
			form.setValues({
				name: license.name,
				accountId: license.accountId._id,
				os: license.os,
				licenseTypeId: license.licenseTypeId?._id || "",
				isUnlimited: license.isUnlimited || false,
				expirationDate:
					license.isUnlimited || !license.expirationDate
						? ""
						: toDateInputValue(new Date(license.expirationDate)),
				tags: license.tags || [],
			});
		} else if (opened) {
			form.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [license?._id, opened]);

	const handleSubmit = (values: LicenseFormData) => {
		onSubmit(values);
	};

	const handleClose = () => {
		form.reset();
		onClose();
	};

	useEffect(() => {
		if (opened) {
			// Auto-select account if there's only one account available (for new licenses only)
			if (!license && accounts.length === 1 && !form.getValues().accountId) {
				const singleAccount = accounts[0];
				handleAccountChange(singleAccount.value);
			}
		}
		setTimeout(() => {
			setActiveFocus(opened);
		}, 5);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [opened]);

	const handleAccountChange = (accountId: string) => {
		form.setFieldValue("accountId", accountId);
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={license ? "Edit License" : "Create License"}
			size="md">
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="md">
					<FocusTrap active={activeFocus}>
						<TextInput
							label="Name"
							placeholder="Enter license name"
							required
							{...form.getInputProps("name")}
						/>
					</FocusTrap>

					<Select
						label="Account"
						placeholder="Select account"
						required
						data={accounts}
						value={form.getInputProps("accountId").value}
						onChange={(value) => handleAccountChange(value || "")}
						error={form.getInputProps("accountId").error}
					/>

					<Select
						label="License Type"
						placeholder="Select license type"
						required
						data={licenseTypes}
						{...form.getInputProps("licenseTypeId")}
					/>

					<Select
						label="Operating System"
						placeholder="Select OS"
						required
						data={OS_OPTIONS}
						{...form.getInputProps("os")}
					/>

					<Stack gap="xs">
						<TextInput
							label="Expiration Date"
							type="date"
							required={!form.getValues().isUnlimited}
							disabled={form.getValues().isUnlimited}
							{...form.getInputProps("expirationDate")}
						/>

						<Checkbox
							label="Unlimited (no expiration date)"
							{...form.getInputProps("isUnlimited", { type: "checkbox" })}
						/>

						{form.getValues().isUnlimited && (
							<Alert
								variant="light"
								color="orange"
								title="Risk Warning"
								icon={<IconAlertTriangle size={18} />}>
								Unlimited licenses never expire and cannot be automatically
								revoked by the system. Use with caution — only assign unlimited
								licenses to trusted, long-term accounts.
							</Alert>
						)}

						<ExpirationQuickSet
							disabled={form.getValues().isUnlimited}
							onSelect={(date) => form.setFieldValue("expirationDate", date)}
						/>
					</Stack>
					{license && (
						<Stack gap={2}>
							<Text size="sm">
								Activated: {toDisplayDate(license.activationDate)}
							</Text>
							<Text
								size="sm"
								c="dimmed">
								First activated: {toDisplayDate(license.firstActivatedAt)}
							</Text>
						</Stack>
					)}

					<TagsInput
						label="Tags"
						placeholder="Add tags"
						{...form.getInputProps("tags")}
					/>

					<Group
						justify="flex-end"
						mt="lg">
						<Button
							variant="light"
							onClick={handleClose}>
							Cancel
						</Button>
						<Button
							type="submit"
							loading={loading}>
							{license ? "Update" : "Create"}
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}

export default LicenseModal;
